import { Tabs } from 'antd';
import RunningTotal from 'components/runningtotal/running-total';
import { useElementHeight } from 'helpers/useElementHeight';
import React, { RefObject, useEffect, useRef } from 'react';
import './options-sidebar.css';
import { useAppState } from 'state/appstate/appstate';
import TabPane from './tabpane';
import shallow from 'zustand/shallow';
import { Option } from 'api';
import { useUiState } from 'state/ui-state/ui-state';
import { ActionType, StyleAssetType } from '@swd_admin/cdc-viewer';
import { useViewerState } from 'state/viewer-state/viewer-state';
import { getDefaultConfigurationRenderParameters } from 'viewer/viewerActions';

const footRatio = 12;

export const filterAvailableOptions = (options: Option[] = []): Option[] => {
  const { uState } = useAppState.getState();
  const { depth } = uState?.dimensions || {};
  const depthFoot = depth! / footRatio;
  const renderParameters = getDefaultConfigurationRenderParameters();
  const styleRoofPitch = renderParameters?.style_roof_pitch;
  return options.filter((o) => {
    if (o.optionTypeId === 'roof') {
      const { extraInfo = {} } = JSON.parse(o.ghModelJson);
      const {
        minDepth = 0,
        maxDepth = Infinity,
        roofPitch = -Infinity,
      } = extraInfo;
      return (
        depthFoot >= minDepth &&
        maxDepth >= depthFoot &&
        styleRoofPitch > roofPitch
      );
    }
    return true;
  });
};

const OptionsSidebar: React.FunctionComponent = () => {
  let ref: RefObject<HTMLDivElement> = useRef(null);
  const runningTotalHeight = useElementHeight(ref);
  const { focusType, setFocusType } = useUiState();
  const { viewer, viewerState } = useViewerState();
  const { activeTabKey, setActiveTabKey } = useUiState();
  const { options, optionTypes, optionFolders } = useAppState(
    (state) => ({
      options: filterAvailableOptions(state.styleProduct.style?.options),
      optionTypes: state.styleProduct.style?.optionTypes,
      optionFolders: state.styleProduct.style?.optionFolders,
    }),
    shallow
  );

  useEffect(() => {
    const focus = activeTabKey === 'Roof' ? StyleAssetType.Roof : StyleAssetType.Wall;
    if (focus !== focusType  && viewer) {
      viewer.dispatch({
        type: ActionType.SetFocus,
        focusType: focus,
      });
      setFocusType(focus);
    }
  }, [activeTabKey, viewerState]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        background: Theme['@white'],
      }}
    >
      <Tabs
        tabPosition={'left'}
        style={{ height: '100%' }}
        defaultActiveKey={activeTabKey}
        destroyInactiveTabPane={true}
        onChange={(activeKey: string) => {
          setActiveTabKey(activeKey);
        }}
      >
        {optionTypes &&
          optionTypes.map((oType) => {
            return (
              <Tabs.TabPane
                tab={oType.displayName
                  ?.toUpperCase()
                  ?.split(' ')
                  .map((part) => (
                    <div>{part}</div>
                  ))}
                key={oType.displayName}
                style={{ paddingRight: 0, height: '100%' }}
              >
                {
                  <TabPane
                    title={oType.displayName}
                    description={oType.displayName}
                    filters={optionFolders?.filter((folder) =>
                      oType.optionFolderIds?.includes(folder.id)
                    )}
                    options={[
                      ...options?.filter(
                        (option) => oType.id === option.optionTypeId && option.isVisible
                      ),
                    ]}
                    runningTotalHeight={runningTotalHeight}
                  />
                }
              </Tabs.TabPane>
            );
          })}
      </Tabs>
      <div ref={ref}>
        <RunningTotal />
      </div>
    </div>
  );
};

export default OptionsSidebar;
